// ═════════════════════════════════════════════════════════════════════════════
//  SPACY COACH · OBSERVABILIDAD
//
//  Rob, sobre la primera version: «no es una toma de decisiones de como va, el
//  conteo de tokens nada. Este sistema tiene que ser mis ojos, mis decisiones».
//  Y sobre la segunda: «se ve horrible».
//
//  Las dos criticas eran la misma. Un panel que no se lee no se mira, y uno que
//  no se mira no sirve para decidir: la forma AQUI es parte de la funcion.
//
//  ── LO QUE CAMBIA RESPECTO A LA VERSION ANTERIOR ────────────────────────────
//  · Habla el idioma del Command Center —neumorfismo, cian, Inter y JetBrains
//    Mono— en vez de inventarse uno. Los tokens llegan por `t` y `HICOLOR`,
//    que es lo que pasa el panel a cada pestaña y yo estaba ignorando.
//  · El titular deja de ser una fila de numeros y pasa a ser UNA BARRA: la voz
//    contra el razonamiento. El reparto es la conclusion, no un detalle.
//  · Aparecen los treinta dias. Un total no dice si el gasto sube o si hubo un
//    dia raro; la serie si, y el dato ya estaba guardado.
//  · Modelos y asistentes se ordenan y se pintan con barra: cual manda se ve
//    sin comparar cifras.
//
//  ── EL NUMERO QUE MANDA ─────────────────────────────────────────────────────
//  Medido sobre 161 llamadas reales: LA VOZ ES EL 86% del gasto del coach. El
//  razonamiento entero —DeepSeek y sus dos respaldos— es el 14%. Por eso el
//  reparto va arriba y en grande: cualquier decision de coste que empiece por
//  el modelo de texto esta mirando al sitio equivocado.
//
//  ── SE MIRA, NO SE TOCA ─────────────────────────────────────────────────────
//  Solo lectura, y cada apertura de una conversacion queda en
//  `admin_audit_logs`. Lo que se protege no es el dato: es la intimidad del
//  alumno.
// ═════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Bot, Coins, MessageSquare, Mic, RefreshCw, Search, User, Users,
} from 'lucide-react';

import api from '../../../../api/apiClient';
import './SpacyCoachTab.css';

const dinero = n => `$${(n || 0).toFixed(4)}`;
const miles = n => (n || 0).toLocaleString('es-MX');

const cuando = iso => (iso
  ? new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    })
  : '');

const soloDia = iso => (iso
  ? new Date(`${iso}T12:00:00`).toLocaleDateString('es-MX',
      { day: '2-digit', month: 'short' })
  : '');

export default function SpacyCoachTab({ t, HICOLOR, dark = true }) {
  const [resumen, setResumen] = useState(null);
  const [alumnos, setAlumnos] = useState([]);
  const [elegido, setElegido] = useState(null);
  const [charla, setCharla] = useState(null);
  const [buscando, setBuscando] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  // El componente puede montarse suelto en un banco de pruebas, sin el panel
  // que le pasa los tokens. Se cae de pie con los del tema oscuro.
  const tk = t || {
    bg: dark ? '#1a1a1a' : '#e0e5ec',
    tx: dark ? '#e8e8f0' : '#1a1a2e',
    tx2: dark ? '#7a7a9a' : '#7a8499',
    ac: dark ? '#00FFFF' : '#ffff00',
    ok: dark ? '#00ff88' : '#00cc66',
    er: dark ? '#ff6b6b' : '#f87171',
    nmd: dark ? '-6px 6px 12px #0a0a0a, 6px -6px 12px #2a2a2a'
              : '-6px 6px 12px #a3b1c6, 6px -6px 12px #fff',
    nsm: dark ? '-3px 3px 8px #0a0a0a, 3px -3px 8px #2a2a2a'
              : '-3px 3px 8px #a3b1c6, 3px -3px 8px #fff',
    nxs: dark ? '-2px 2px 5px #0a0a0a, 2px -2px 5px #2a2a2a'
              : '-2px 2px 5px #a3b1c6, 2px -2px 5px #fff',
    ism: dark ? 'inset -3px 3px 8px #0a0a0a, inset 3px -3px 8px #2a2a2a'
              : 'inset -3px 3px 8px #a3b1c6, inset 3px -3px 8px #fff',
    ixs: dark ? 'inset -2px 2px 5px #0a0a0a, inset 2px -2px 5px #2a2a2a'
              : 'inset -2px 2px 5px #a3b1c6, inset 2px -2px 5px #fff',
  };
  const acento = HICOLOR || tk.ac;

  const vars = {
    '--sc-bg': tk.bg,
    '--sc-tx': tk.tx,
    '--sc-tx2': tk.tx2,
    '--sc-ac': acento,
    '--sc-ok': tk.ok,
    '--sc-er': tk.er,
    '--sc-nmd': tk.nmd,
    '--sc-nsm': tk.nsm,
    '--sc-nxs': tk.nxs,
    '--sc-ism': tk.ism,
    '--sc-ixs': tk.ixs,
    '--sc-linea': dark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.08)',
  };

  const carga = () => {
    setCargando(true);
    Promise.all([
      api.get('/api/coach/admin/resumen').then(r => setResumen(r.data)),
      api.get('/api/coach/admin/alumnos').then(r => setAlumnos(r.data?.alumnos || [])),
    ])
      .then(() => setError(''))
      .catch(() => setError('No se pudo cargar. ¿Está levantado el backend?'))
      .finally(() => setCargando(false));
  };

  useEffect(carga, []);

  useEffect(() => {
    if (!elegido) { setCharla(null); return; }
    setCharla({ cargando: true });
    api.get(`/api/coach/admin/conversacion/${elegido}`)
      .then(r => setCharla(r.data))
      .catch(() => setCharla({ error: true }));
  }, [elegido]);

  // Por defecto llegan por quien hablo hace menos. Con "gasto" se ponen
  // arriba los que queman credito, que es la lista que hay que mirar cuando lo
  // que se busca es un mal uso y no una conversacion.
  const [orden, setOrden] = useState('reciente');

  const filtrados = useMemo(() => {
    const q = buscando.trim().toLowerCase();
    const base = !q ? alumnos
      : alumnos.filter(a => (a.nombre || '').toLowerCase().includes(q)
                         || (a.email || '').toLowerCase().includes(q));
    if (orden === 'gasto') {
      return [...base].sort((x, y) => (y.coste_mes || 0) - (x.coste_mes || 0));
    }
    return base;
  }, [alumnos, buscando, orden]);

  const d = resumen?.dinero;
  const total = d?.total || 0;
  const pctVoz = total ? (d.voz / total) * 100 : 0;
  const llamadas = d?.por_modelo?.reduce((s, m) => s + m.llamadas, 0) || 0;
  const fallos = resumen?.salud?.turnos_fallidos || 0;
  const conDato = resumen?.salud?.turnos_con_dato || 0;
  const dias = d?.por_dia || [];
  const pico = Math.max(...dias.map(x => x.coste), 0.0000001);
  const topModelo = Math.max(...(d?.por_modelo || []).map(m => m.coste), 0.0000001);
  const topAsis = Math.max(...(resumen?.asistentes || []).map(a => a.veces), 1);

  const Tarjeta = ({ icono: Ico, titulo, valor, pie, color }) => (
    <div className="scoach-tarjeta">
      <div className="scoach-rotulo"><Ico size={12} /> {titulo}</div>
      <div className="scoach-tarjeta-valor scoach-cifra"
           style={{ color: color || tk.tx }}>{valor}</div>
      {pie && <div className="scoach-tarjeta-pie">{pie}</div>}
    </div>
  );

  return (
    <div className="scoach" style={vars}>

      {/* ── EL TITULAR ──────────────────────────────────────────────────── */}
      <div className="scoach-caja scoach-titular">
        <div className="scoach-total">
          <div className="scoach-rotulo"><Coins size={12} /> Gasto del coach</div>
          <div className="scoach-total-cifra scoach-cifra">{dinero(total)}</div>
          <div className="scoach-tarjeta-pie">
            {miles(llamadas)} llamadas a la IA
          </div>
        </div>

        {/* La barra es la conclusion: la voz se lleva casi todo. Con dos cifras
            sueltas hay que dividir mentalmente; asi se ve antes de leer. */}
        <div className="scoach-reparto">
          <div className="scoach-barra">
            <div className="scoach-tramo scoach-tramo-voz"
                 style={{ flexGrow: Math.max(d?.voz || 0, 0.0001) }}>
              {pctVoz >= 12 ? `VOZ ${Math.round(pctVoz)}%` : ''}
            </div>
            <div className="scoach-tramo scoach-tramo-raz"
                 style={{ flexGrow: Math.max(d?.razonamiento || 0, 0.0001) }}>
              {100 - pctVoz >= 12 ? `RAZONAMIENTO ${Math.round(100 - pctVoz)}%` : ''}
            </div>
          </div>
          <div className="scoach-pies">
            <span style={{ color: tk.er }}>
              <b className="scoach-mono">{dinero(d?.voz)}</b> en voz
            </span>
            <span style={{ color: tk.ok }}>
              <b className="scoach-mono">{dinero(d?.razonamiento)}</b> en pensar
            </span>
          </div>
        </div>
      </div>

      {/* ── LOS TREINTA DIAS ────────────────────────────────────────────── */}
      {dias.length > 1 && (
        <div className="scoach-caja">
          <div className="scoach-rotulo">
            Gasto por día · últimos {dias.length}
          </div>
          <div className="scoach-serie">
            {dias.map(x => (
              <div
                key={x.dia}
                className={`scoach-col${x.coste >= pico ? ' scoach-col-pico' : ''}`}
                style={{ height: `${Math.max(4, (x.coste / pico) * 100)}%` }}
                title={`${soloDia(x.dia)} · ${dinero(x.coste)} · ${x.llamadas} llamadas`}
              />
            ))}
          </div>
          <div className="scoach-pies" style={{ marginTop: 6 }}>
            <span>{soloDia(dias[0]?.dia)}</span>
            <span>día más caro: <b className="scoach-mono">{dinero(pico)}</b></span>
            <span>{soloDia(dias[dias.length - 1]?.dia)}</span>
          </div>
        </div>
      )}

      {/* ── LAS CIFRAS ──────────────────────────────────────────────────── */}
      <div className="scoach-cifras">
        <Tarjeta icono={Mic} titulo="La voz" valor={dinero(d?.voz)}
                 color={tk.er} pie={`${Math.round(pctVoz)}% de la factura`} />
        <Tarjeta icono={MessageSquare} titulo="Razonamiento"
                 valor={dinero(d?.razonamiento)} color={tk.ok}
                 pie="DeepSeek y sus respaldos" />
        <Tarjeta icono={Users} titulo="Alumnos"
                 valor={miles(resumen?.uso?.alumnos)}
                 pie={`${miles(resumen?.uso?.mensajes)} mensajes guardados`} />
        <Tarjeta icono={Bot} titulo="Rutinas"
                 valor={miles(resumen?.rutinas?.creadas)}
                 pie={`${miles(resumen?.rutinas?.alumnos)} alumnos con rutina`} />
        <Tarjeta icono={AlertTriangle} titulo="Turnos fallidos" valor={fallos}
                 color={fallos ? tk.er : tk.ok}
                 pie={conDato ? `de ${miles(conDato)} con dato` : 'sin datos todavía'} />
      </div>

      {/* ── MODELOS Y ASISTENTES ────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {Boolean(d?.por_modelo?.length) && (
          <div className="scoach-caja" style={{ flex: '1 1 300px', minWidth: 270 }}>
            <div className="scoach-rotulo">Quién se lleva el dinero</div>
            <div className="scoach-lista">
              {d.por_modelo.map(m => (
                <div className="scoach-fila-barra" key={m.modelo}>
                  <span className="scoach-relleno"
                        style={{ width: `${(m.coste / topModelo) * 100}%`,
                                 background: m.es_voz ? tk.er : acento }} />
                  <span className="scoach-nombre"
                        style={{ color: m.es_voz ? tk.er : tk.tx }}>
                    {m.modelo}
                  </span>
                  <span className="scoach-dato scoach-mono">{dinero(m.coste)}</span>
                  {/* La voz se cobra por segundo de audio, no por tokens: un
                      "0 tokens" en su fila se lee como un error del sistema. */}
                  <span className="scoach-dato">
                    {m.es_voz
                      ? `${miles(m.llamadas)} audios`
                      : `${miles(m.entrada + m.salida)} tk`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {Boolean(resumen?.asistentes?.length) && (
          <div className="scoach-caja" style={{ flex: '1 1 300px', minWidth: 270 }}>
            {/* Se sabia cuales estan construidos. Lo que no se sabia es cuales
                se LLAMAN, y uno que nunca se llama es una descripcion que no
                funciona. */}
            <div className="scoach-rotulo">Asistentes que usa de verdad</div>
            <div className="scoach-lista">
              {resumen.asistentes.slice(0, 8).map(a => (
                <div className="scoach-fila-barra" key={a.asistente}>
                  <span className="scoach-relleno"
                        style={{ width: `${(a.veces / topAsis) * 100}%` }} />
                  <span className="scoach-nombre">{a.asistente}</span>
                  <span className="scoach-dato scoach-mono"
                        style={{ color: acento }}>{miles(a.veces)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p style={{ margin: 0, font: '700 12px Inter, sans-serif', color: tk.er }}>
          {error}
        </p>
      )}

      {/* ── LAS CONVERSACIONES ──────────────────────────────────────────── */}
      <div className="scoach-abajo">
        <aside className="scoach-lateral">
          <div style={{ display: 'flex', gap: 8 }}>
            <div className="scoach-buscador">
              <Search size={14} style={{ color: tk.tx2, flexShrink: 0 }} />
              <input value={buscando} onChange={e => setBuscando(e.target.value)}
                     placeholder="Buscar alumno…" />
            </div>
            <button className="scoach-boton" onClick={carga} title="Recargar">
              <RefreshCw size={14} />
            </button>
          </div>

          <div className="scoach-rotulo" style={{ justifyContent: 'space-between' }}>
            <span>{cargando ? 'Cargando…' : `${filtrados.length} con conversación`}</span>
            <button
              onClick={() => setOrden(o => (o === 'gasto' ? 'reciente' : 'gasto'))}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                font: 'inherit', letterSpacing: 'inherit',
                color: orden === 'gasto' ? acento : 'inherit',
              }}
            >
              {orden === 'gasto' ? '↓ por gasto' : '↓ reciente'}
            </button>
          </div>

          <div className="scoach-alumnos">
            {filtrados.map(a => (
              <button key={a.user_id}
                      className={`scoach-alumno${a.user_id === elegido ? ' scoach-alumno-on' : ''}`}
                      onClick={() => setElegido(a.user_id)}>
                <div className="scoach-alumno-fila">
                  <span className="scoach-alumno-nombre">{a.nombre}</span>
                  <span className="scoach-dato scoach-mono" style={{ color: acento }}>
                    {a.mensajes}
                  </span>
                </div>
                {/* Lo que gasta y las señales de mal uso, pegadas al nombre.
                    Rob: «llevamos un registro de que uso se le esta dando y
                    nos blindamos contra malos usos». Cuantos mensajes lleva no
                    dice nada de eso; lo que GASTA y lo que le FALLA, si. */}
                <div className="scoach-tarjeta-pie">
                  {cuando(a.ultimo)} · {a.suyos} suyos
                  {a.hoy > 0 && <> · <b style={{ color: acento }}>{a.hoy} hoy</b></>}
                </div>
                <div className="scoach-tarjeta-pie" style={{ marginTop: 1 }}>
                  <span className="scoach-mono">{dinero(a.coste_mes)}</span> este mes
                  {a.fallidos > 0 && (
                    <span style={{ color: tk.er }}> · {a.fallidos} fallidos</span>
                  )}
                </div>
              </button>
            ))}
            {!cargando && !filtrados.length && (
              <p className="scoach-vacio" style={{ margin: '6px 0', textAlign: 'left' }}>
                Todavía no hay conversaciones guardadas. Se guardan desde el
                primer mensaje que se cruce con el coach a partir de ahora; las
                de antes no se recuperan porque nunca llegaron a escribirse.
              </p>
            )}
          </div>
        </aside>

        <section className="scoach-charla">
          {!elegido && !charla && (
            <div className="scoach-vacio">
              <MessageSquare size={26} style={{ opacity: .45 }} />
              <p style={{ marginTop: 8 }}>
                Elige un alumno para leer lo que le dice el coach.
              </p>
            </div>
          )}

          {charla?.cargando && <div className="scoach-vacio">Abriendo…</div>}
          {charla?.error && (
            <div className="scoach-vacio" style={{ color: tk.er }}>
              No se pudo abrir esa conversación.
            </div>
          )}

          {charla?.alumno && (
            <>
              <header className="scoach-charla-cabeza">
                <User size={15} style={{ color: acento }} />
                <span style={{ font: '800 13px Inter, sans-serif' }}>
                  {charla.alumno.nombre}
                </span>
                <span className="scoach-tarjeta-pie">{charla.alumno.email}</span>
                <span className="scoach-dato" style={{ marginLeft: 'auto' }}>
                  {charla.mensajes.length} mensajes
                </span>
              </header>

              <div className="scoach-mensajes">
                {charla.mensajes.map((m, i) => {
                  const suyo = m.de === 'alumno';
                  const fallo = m.ok === false;
                  return (
                    <div key={i}
                         className={'scoach-burbuja'
                           + (suyo ? ' scoach-burbuja-alumno' : '')
                           + (fallo ? ' scoach-burbuja-fallo' : '')}
                         style={{ animationDelay: `${Math.min(i, 12) * 18}ms` }}>
                      {/* La telemetria del turno, pegada al mensaje. Es lo que
                          convierte "leer la charla" en "auditarla". */}
                      <div className="scoach-meta">
                        <span>{suyo ? 'Alumno' : 'Coach'}</span>
                        <span>{cuando(m.cuando)}</span>
                        {!suyo && m.modelo && (
                          <span style={{ color: acento }}>{m.modelo}</span>
                        )}
                        {!suyo && Boolean(m.coste_usd) && (
                          <span className="scoach-mono">{dinero(m.coste_usd)}</span>
                        )}
                        {fallo && <span style={{ color: tk.er }}>falló</span>}
                      </div>

                      <div className="scoach-texto">{m.texto}</div>

                      {Boolean(m.tarjeta || m.clase || m.asistentes?.length) && (
                        <div className="scoach-sellos">
                          {m.tarjeta && (
                            <span style={{ color: acento }}>📊 {m.tarjeta.titulo}</span>
                          )}
                          {m.clase && (
                            <span style={{ color: tk.ok }}>▶ {m.clase.tipo}</span>
                          )}
                          {Boolean(m.asistentes?.length) && (
                            <span style={{ color: tk.tx2 }}>
                              🔧 {m.asistentes.join(' · ')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {!charla.mensajes.length && (
                  <div className="scoach-vacio">Sin mensajes guardados.</div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
